/**
 * Typed form for the `general-interaction` encounter type.
 *
 * Steps: Type → Notes (general only) → Details → (Next-Task Assignment when
 * activity-driven). No clinical impressions, no questionnaires, no scribe,
 * no goal progress, no enrollment chain.
 *
 * @agentic connections
 */

import { Skeleton, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { forwardRef, useMemo } from "react";
import type { Encounter } from "@medplum/fhirtypes";

import { CallDocumentationField } from "@/components/domain/care-time/CallDocumentationField";
import { useCallTimeCareSelection } from "../hooks/useCallTimeCareSelection";

import { getCarePlanActivityDisplay } from "@benchmark/core/constants";

import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { useFormSubmission } from "@benchmark/ui/hooks";
import type {
  EncounterActivityContext,
  GeneralInteractionFormData,
} from "@benchmark/core/types";
import type { AppliedBusinessRule } from "@benchmark/core/resolvers";

import type { EncounterFormRef } from "../EncounterFormShell";
import { useNonIntakeFormScaffold } from "../hooks/useNonIntakeFormScaffold";
import { EncounterDetailsStep } from "../shared/EncounterDetailsStep";
import { EncounterTypeStep } from "../shared/EncounterTypeStep";
import { GeneralNoteStep } from "../shared/GeneralNoteStep";
import { NextTaskAssignmentStep } from "../shared/NextTaskAssignmentStep";
import {
  buildDirectionOptions,
  buildMethodOptions,
} from "../shared/encounterIcons";
import { useGeneralInteractionSubmit } from "./useGeneralInteractionSubmit";

const METHOD_OPTIONS = buildMethodOptions();
const DIRECTION_OPTIONS = buildDirectionOptions();

/**
 * Props for `GeneralInteractionForm`. Mirrors the dispatcher's prop bag.
 *
 * @agentic connections
 */
export type GeneralInteractionFormProps = {
  patientId?: string;
  encounter?: Encounter;
  onSuccess?: (encounterId: string) => void;
  onCancel?: () => void;
  activityContext?: EncounterActivityContext;
  restrictToEncounterTypes?: string[];
  nextActivityLabel?: string;
  nextActivityDefaultOwner?: { ref: string; display: string };
  nextTaskOwnerOverride?: { ref: string; display: string } | null;
  onNextTaskOwnerOverrideChange?: (
    owner: { ref: string; display: string } | null,
  ) => void;
  nextActivityAppliedRule?: AppliedBusinessRule;
  isResolvingNextTaskOwner?: boolean;
};

/**
 * Typed form for `general-interaction` encounters.
 *
 * @agentic connections
 */
export const GeneralInteractionForm = forwardRef<
  EncounterFormRef,
  GeneralInteractionFormProps
>(function GeneralInteractionForm(
  {
    patientId: propPatientId,
    encounter: propEncounter,
    onSuccess,
    onCancel,
    activityContext,
    restrictToEncounterTypes,
    nextActivityLabel,
    nextActivityDefaultOwner,
    nextTaskOwnerOverride,
    onNextTaskOwnerOverrideChange,
    nextActivityAppliedRule,
    isResolvingNextTaskOwner = false,
  },
  ref,
) {
  const form = useForm<GeneralInteractionFormData>({
    initialValues: {
      "encounter-type": "general-interaction",
      period: { start: "", end: "" },
      "encounter-method": "",
      "encounter-inout": "",
      comment: "",
    },
  });

  const {
    patient,
    existingEncounter,
    isEditing,
    isLoadingEdit,
    practitionerRoles,
    ConfirmationModal,
    markAsClean,
  } = useNonIntakeFormScaffold({
    type: "general-interaction",
    form,
    propPatientId,
    propEncounter,
    activityContext,
    nextTaskOwnerOverride,
    ref,
  });

  const careTime = useCallTimeCareSelection();

  const handleSubmit = useGeneralInteractionSubmit({
    patient,
    existingEncounter,
    practitionerRoles,
    activityContext,
    onSuccess,
    onSubmitted: markAsClean,
    getCareTimeMinutes: careTime.getCareTimeMinutes,
    getCareTimeCommunicationId: careTime.getCareTimeCommunicationId,
    getCareTimeEffectivePeriod: careTime.getCareTimeEffectivePeriod,
  });

  const { createSubmissionHandler, isSubmitting } = useFormSubmission();

  const steps = useMemo(
    () => [
      {
        id: "type",
        title: "Type",
        subtitle: isEditing
          ? "Interaction type. (Read-Only)"
          : "Select the type of interaction.",
        readOnly: isEditing,
        showWhen: () =>
          !activityContext?.activityCode &&
          !isEditing &&
          !(restrictToEncounterTypes && restrictToEncounterTypes.length === 1),
        requiredFields: ["encounter-type"] as string[],
      },
      {
        id: "notes",
        title: "Notes",
        subtitle: "Document the encounter",
        validate: (values: GeneralInteractionFormData) =>
          !values.comment || values.comment.trim() === ""
            ? { comment: "General note is required" }
            : null,
      },
      {
        id: "details",
        title: "Details",
        subtitle: "Review and confirm the interaction details.",
        requiredFields: [
          "period.start",
          "period.end",
          "encounter-method",
          "encounter-inout",
        ] as string[],
      },
      ...(activityContext
        ? [
            {
              id: "next-task-assignment",
              title: "Assignment",
              subtitle: "Review next task assignment",
            },
          ]
        : []),
    ],
    [activityContext, isEditing, restrictToEncounterTypes],
  );

  const submitText = activityContext?.activityCode
    ? getCarePlanActivityDisplay(activityContext.activityCode)
    : isEditing
      ? "Update Interaction"
      : "Save Interaction";

  if (!patient || isLoadingEdit) {
    return (
      <div data-testid="general-interaction-form">
        <Stack gap="md">
          <Skeleton height={32} width="40%" />
          <Skeleton height={14} width="30%" />
          <Skeleton height={36} />
          <Skeleton height={14} width="25%" />
          <Skeleton height={36} />
          <Skeleton height={44} width="200px" />
        </Stack>
        <ConfirmationModal />
      </div>
    );
  }

  return (
    <div data-testid="general-interaction-form">
      <SteppedForm
        form={form}
        steps={steps}
        onSubmit={createSubmissionHandler(handleSubmit)}
        isSubmitting={isSubmitting}
        submitText={submitText}
        onCancel={onCancel}
      >
        <FormStep id="type">
          <EncounterTypeStep
            value={form.values["encounter-type"]}
            allowedTypes={restrictToEncounterTypes ?? ["general-interaction"]}
            error={form.errors["encounter-type"] as string | undefined}
            readOnly={isEditing}
            onChange={(value) => {
              if (value === "general-interaction") {
                form.setFieldValue("encounter-type", "general-interaction");
              }
            }}
          />
        </FormStep>

        <FormStep id="notes">
          <GeneralNoteStep form={form} />
        </FormStep>

        <FormStep id="details">
          <Stack gap="md">
            <EncounterDetailsStep
              form={form}
              methodOptions={METHOD_OPTIONS}
              directionOptions={DIRECTION_OPTIONS}
              billableOptions={[]}
              requiresBillable={false}
            />
            {!activityContext && (
              <CallDocumentationField
                patientId={patient?.id}
                minutes={careTime.minutes}
                onMinutesChange={careTime.setMinutes}
                selectedCommunicationId={careTime.selectedCall?.communicationId}
                onSelectCall={careTime.selectCall}
              />
            )}
          </Stack>
        </FormStep>

        <FormStep id="next-task-assignment">
          <Stack gap="md">
            <NextTaskAssignmentStep
              form={form}
              nextActivityLabel={nextActivityLabel}
              nextActivityDefaultOwner={nextActivityDefaultOwner}
              nextTaskOwnerOverride={nextTaskOwnerOverride}
              onNextTaskOwnerOverrideChange={onNextTaskOwnerOverrideChange}
              nextActivityAppliedRule={nextActivityAppliedRule}
              isResolvingNextTaskOwner={isResolvingNextTaskOwner}
            />
            <CallDocumentationField
              patientId={patient?.id}
              minutes={careTime.minutes}
              onMinutesChange={careTime.setMinutes}
              selectedCommunicationId={careTime.selectedCall?.communicationId}
              onSelectCall={careTime.selectCall}
            />
          </Stack>
        </FormStep>
      </SteppedForm>

      <ConfirmationModal />
    </div>
  );
});
